"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useSession } from "next-auth/react"
import { Home, HardHat, Search, MessageCircle, User, BookOpen, Users, Landmark } from "lucide-react"
import { useSSECounts } from "@/context/SSEContext"

// Pages where the tab bar should not appear
const HIDDEN_ON = ["/login", "/register", "/verify-email"]

export default function BottomTabBar() {
  const pathname = usePathname()
  const router = useRouter()
  const { data: session, status } = useSession()
  const { unreadCount, activityCount: projectActivityCount } = useSSECounts()
  const [searchMenuOpen, setSearchMenuOpen] = useState(false)
  const searchMenuRef = useRef<HTMLDivElement>(null)

  // Close search submenu when navigating away
  useEffect(() => { setSearchMenuOpen(false) }, [pathname])

  // Close on outside tap
  useEffect(() => {
    if (!searchMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(e.target as Node)) {
        setSearchMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [searchMenuOpen])

  if (HIDDEN_ON.some((p) => pathname.startsWith(p))) return null

  const profileHref =
    status === "loading"
      ? "/profile"
      : session?.user?.id
      ? `/profile/${session.user.id}`
      : "/login"

  const isSearchActive =
    pathname.startsWith("/search") || pathname.startsWith("/directory") || pathname.startsWith("/federal")

  return (
    <>
      {/* Search sub-menu — slides up above the tab bar */}
      {searchMenuOpen && (
        <div
          ref={searchMenuRef}
          className="md:hidden fixed bottom-[calc(56px+env(safe-area-inset-bottom))] left-0 right-0 z-50 mx-4 mb-2"
        >
          <div
            className="rounded-2xl border shadow-xl overflow-hidden"
            style={{ background: "#FFFFFF", borderColor: "#E8E2D9" }}
          >
            <button
              onClick={() => { router.push("/search"); setSearchMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors border-b"
              style={{ borderColor: "#E8E2D9" }}
            >
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-sm text-gray-800">Find People</p>
                <p className="text-xs text-gray-500">Search tradespeople &amp; companies</p>
              </div>
            </button>
            <button
              onClick={() => { router.push("/directory"); setSearchMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors border-b"
              style={{ borderColor: "#E8E2D9" }}
            >
              <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-sm text-gray-800">Directory</p>
                <p className="text-xs text-gray-500">Browse top-ranked pros by trade &amp; city</p>
              </div>
            </button>
            <button
              onClick={() => { router.push("/federal/opportunities"); setSearchMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <Landmark className="w-4 h-4 text-green-700" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-sm text-gray-800">Federal Bids</p>
                <p className="text-xs text-gray-500">Active government contracting opportunities</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t flex items-stretch"
        style={{
          background: "#F7F4EE",
          borderColor: "#E8E2D9",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Home */}
        <TabLink href="/feed" label="Home" icon={Home} active={pathname === "/feed"} />

        {/* Projects */}
        <TabLink
          href="/projects"
          label="Projects"
          icon={HardHat}
          active={pathname.startsWith("/projects")}
          badge={projectActivityCount}
          badgeRed
        />

        {/* Search — tap to open sub-menu */}
        <button
          onClick={() => setSearchMenuOpen((v) => !v)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors"
          style={{ color: isSearchActive || searchMenuOpen ? "#C8922A" : "#9CA3AF" }}
        >
          <Search
            size={22}
            strokeWidth={isSearchActive || searchMenuOpen ? 2.2 : 1.7}
            style={{ color: isSearchActive || searchMenuOpen ? "#C8922A" : "#9CA3AF" }}
          />
          <span>Search</span>
        </button>

        {/* Messages */}
        <TabLink
          href="/messages"
          label="Messages"
          icon={MessageCircle}
          active={pathname.startsWith("/messages")}
          badge={unreadCount}
          badgeRed
        />

        {/* Profile */}
        <TabLink
          href={profileHref}
          label="Profile"
          icon={User}
          active={pathname.startsWith("/profile")}
        />
      </nav>
    </>
  )
}

// ─── Sub-component ─────────────────────────────────────────────────────────────

function TabLink({
  href, label, icon: Icon, active, badge, badgeRed,
}: {
  href: string
  label: string
  icon: React.ElementType
  active: boolean
  badge?: number
  badgeRed?: boolean
}) {
  const hasBadge = !!badge && badge > 0
  return (
    <Link
      href={href}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors"
      style={{ color: active ? "#2D3B44" : "#9CA3AF" }}
    >
      <div className="relative">
        <Icon
          size={22}
          strokeWidth={active ? 2.2 : 1.7}
          style={{ color: hasBadge && badgeRed ? "#EF4444" : active ? "#C8922A" : "#9CA3AF" }}
        />
        {hasBadge && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full h-2 w-2" />
        )}
      </div>
      <span>{label}</span>
    </Link>
  )
}
